import PencilIcon from '@/icons/pencil.svg?react';
import { useTranslation } from 'react-i18next';
import MiniLoader from './MiniLoader/MiniLoader';
import Button from './Button';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  isEditing: boolean;
  title: string;
  onClickEdit: () => void;
  onClickUpdate: () => void;
  onClickCancel: () => void;
  isLoading: boolean;
  hasCustomAccountNumbers: boolean;
  className?: string;
}

export const COATabHeader = ({
  isEditing,
  title,
  onClickEdit,
  onClickUpdate,
  onClickCancel,
  isLoading,
  hasCustomAccountNumbers,
  className = '',
  children,
}: PropsWithChildren<Props>) => {
  const showBtnRef = useRef(false);
  const [isHeaderBtnShown, setIsHeaderBtnShown] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const onScroll = () => {
      if (document.documentElement.scrollTop > 68 && !showBtnRef.current) {
        setIsHeaderBtnShown(true);
        showBtnRef.current = true;
      }
      if (document.documentElement.scrollTop <= 68 && showBtnRef.current) {
        setIsHeaderBtnShown(false);
        showBtnRef.current = false;
      }
    };

    document.addEventListener('scroll', onScroll);

    return () => {
      document.removeEventListener('scroll', onScroll);
    };
  }, []);

  const container = document.getElementById('coa_header_btn');

  return (
    <div
      className={`mb-6 flex items-center justify-between gap-x-4 ${className}`}
    >
      <div>
        <div className='text-h3 font-black' data-testid='COATabHeader-title'>
          {t(title)}
        </div>
        {children}
      </div>
      <Btns
        isEditing={isEditing}
        onClickCancel={onClickCancel}
        isLoading={isLoading}
        hasCustomAccountNumbers={hasCustomAccountNumbers}
        onClickUpdate={onClickUpdate}
        onClickEdit={onClickEdit}
      />
      {isHeaderBtnShown &&
        !!container &&
        createPortal(
          <Btns
            isEditing={isEditing}
            onClickCancel={onClickCancel}
            isLoading={isLoading}
            hasCustomAccountNumbers={hasCustomAccountNumbers}
            onClickUpdate={onClickUpdate}
            onClickEdit={onClickEdit}
            name='header'
          />,
          container
        )}
    </div>
  );
};

interface BtnsProps {
  isEditing: boolean;
  onClickCancel: () => void;
  isLoading: boolean;
  onClickUpdate: () => void;
  onClickEdit: () => void;
  hasCustomAccountNumbers: boolean;
  name?: string;
}

const Btns = ({
  isEditing,
  onClickCancel,
  isLoading,
  hasCustomAccountNumbers,
  onClickUpdate,
  onClickEdit,
  name,
}: BtnsProps) => {
  const { t } = useTranslation();
  if (isEditing) {
    return (
      <div className='flex items-center gap-x-2'>
        <div>
          <Button
            styling='outline'
            onClick={onClickCancel}
            disabled={isLoading}
            data-testid={name ? `${name}-cancel_btn` : 'cancel-btn'}
          >
            {t('Cancel')}
          </Button>
        </div>
        <div>
          <Button
            className='flex items-center'
            disabled={isLoading}
            data-testid={name ? `${name}-update-btn` : 'update-btn'}
            onClick={onClickUpdate}
          >
            {isLoading && <MiniLoader className='mr-2' color='gray' />}
            <span>{t('Update')}</span>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='shrink-0'>
      <Button
        data-testid={name ? `${name}-edit-accounts-btn` : 'edit-accounts-btn'}
        styling={hasCustomAccountNumbers ? 'outline' : undefined}
        className='flex items-center gap-x-2'
        onClick={onClickEdit}
      >
        <PencilIcon
          className={
            hasCustomAccountNumbers ? undefined : '[&>g>path]:fill-white'
          }
        />
        <span>{t('Edit accounts')}</span>
      </Button>
    </div>
  );
};
